// frontend/src/pages/PaymentSuccessPage.js
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../utils/axiosConfig";
import API_URL from "../config";

const PaymentSuccessPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [orderStatus, setOrderStatus] = useState(null);
  const [paymentGateway, setPaymentGateway] = useState(null);

  // 從 URL 取得訂單編號 (不同金流有不同參數名稱)
  const queryParams = new URLSearchParams(location.search);
  
  // 永豐金流使用 OrderNo
  const sinopacOrderNo = queryParams.get("OrderNo");
  
  // 綠界金流 - 透過 ClientBackURL 方式返回
  const ecpayOrderNo = queryParams.get("MerchantTradeNo");
  const ecpayRtnCode = queryParams.get("RtnCode");
  
  // 也可能從 HTML5 Web Storage 中獲取由前端在付款前暫存的訂單編號
  // 這是解決綠界重定向問題的備用方案
  const storedOrderNo = localStorage.getItem("currentOrderNo");
  
  // 決定使用哪個訂單編號，優先使用 URL 參數
  const orderNo = sinopacOrderNo || ecpayOrderNo || storedOrderNo || '';

  useEffect(() => {
    // 根據返回參數判斷使用的是哪個金流服務
    if (sinopacOrderNo) {
      setPaymentGateway('sinopac');
    } else if (ecpayOrderNo) {
      setPaymentGateway('ecpay');
      // 如果綠界直接返回了狀態碼，可以直接判斷結果
      if (ecpayRtnCode === '1') {
        setOrderStatus('success');
        return;
      }
    } else if (storedOrderNo) {
      // 從localStorage檢查支付渠道
      const storedGateway = localStorage.getItem("paymentGateway");
      if (storedGateway) {
        setPaymentGateway(storedGateway);
      } else {
        // 如果沒有記錄渠道信息，預設為綠界
        setPaymentGateway('ecpay');
      }
    } else {
      setOrderStatus('unknown');
      return;
    }

    const verifyOrder = async () => {
      if (!orderNo) {
        setOrderStatus("unknown");
        return;
      }

      try {
        let response;
        
        // 根據金流服務選擇不同的查詢API
        if (paymentGateway === 'sinopac') {
          // 永豐金流查詢
          response = await axios.post(
            `${API_URL}/api/order/query`,
            { OrderNo: orderNo },
            { withCredentials: true }
          );
          
          // 永豐金流返回格式
          if (response.data.Status === "S") {
            setOrderStatus("success");
          } else {
            setOrderStatus("pending");
          }
        } else if (paymentGateway === 'ecpay') {
          // 綠界金流查詢
          response = await axios.get(
            `${API_URL}/api/ecpay/query-order/${orderNo}`,
            { withCredentials: true }
          );
          
          // 綠界金流返回格式
          if (response.data.success && response.data.orderData.RtnCode === '1') {
            setOrderStatus("success");
          } else {
            setOrderStatus("pending");
          }
        }
      } catch (error) {
        console.error('訂單查詢失敗:', error);
        setOrderStatus("error");
      }
    };

    if (paymentGateway && orderNo && orderStatus === null) {
      verifyOrder();
    }
  }, [orderNo, paymentGateway, ecpayRtnCode, orderStatus, sinopacOrderNo, ecpayOrderNo, storedOrderNo]);

  // 清理localStorage中的支付相關數據
  useEffect(() => {
    // 當訂單狀態確定後，清理localStorage中的臨時數據
    if (orderStatus !== null) {
      localStorage.removeItem("currentOrderNo");
      localStorage.removeItem("paymentGateway");
    }
  }, [orderStatus]);

  const handleBackHome = () => {
    navigate("/");
  };

  if (orderStatus === null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-lg text-gray-700 dark:text-gray-300">
          {t("paymentSuccess.loading", "正在確認付款狀態...")}
        </p>
      </div>
    );
  }

  if (orderStatus === "error" || orderStatus === "unknown") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-red-50 dark:bg-red-900">
        <h1 className="text-4xl font-bold text-red-800 dark:text-red-200 mb-4">
          {t("paymentSuccess.errorTitle", "訂單狀態異常")}
        </h1>
        <p className="text-lg text-red-700 dark:text-red-300 mb-8">
          {t("paymentSuccess.errorMessage", "無法確認訂單狀態，請聯繫客服。")}
        </p>
        {orderNo && (
          <p className="text-md text-red-600 dark:text-red-400 mb-4">
            {t("paymentSuccess.orderNo", "訂單編號")}: {orderNo}
          </p>
        )}
        <button
          onClick={handleBackHome}
          className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
        >
          {t("paymentSuccess.backHome", "返回首頁")}
        </button>
      </div>
    );
  }

  if (orderStatus === "pending") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-yellow-50 dark:bg-yellow-900">
        <h1 className="text-4xl font-bold text-yellow-800 dark:text-yellow-200 mb-4">
          {t("paymentSuccess.pendingTitle", "訂單處理中")}
        </h1>
        <p className="text-lg text-yellow-700 dark:text-yellow-300 mb-8">
          {t("paymentSuccess.pendingMessage", "您的付款正在處理中，請稍後檢查。")}
        </p>
        {orderNo && (
          <p className="text-md text-yellow-600 dark:text-yellow-400 mb-4">
            {t("paymentSuccess.orderNo", "訂單編號")}: {orderNo}
          </p>
        )}
        <button
          onClick={handleBackHome}
          className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
        >
          {t("paymentSuccess.backHome", "返回首頁")}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-green-50 dark:bg-green-900">
      <h1 className="text-4xl font-bold text-green-800 dark:text-green-200 mb-4">
        {t("paymentSuccess.title", "付款成功！")}
      </h1>
      <p className="text-lg text-green-700 dark:text-green-300 mb-8">
        {t("paymentSuccess.message", "感謝您的付款，您的訂單已成功處理。")}
      </p>
      {orderNo && (
        <p className="text-md text-green-600 dark:text-green-400 mb-4">
          {t("paymentSuccess.orderNo", "訂單編號")}: {orderNo}
        </p>
      )}
      <p className="text-md text-green-600 dark:text-green-400 mb-8">
        {paymentGateway === 'sinopac' 
          ? t("paymentSuccess.viaSinopac", "透過永豐銀行支付") 
          : t("paymentSuccess.viaECPay", "透過綠界金流支付")}
      </p>
      <button
        onClick={handleBackHome}
        className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
      >
        {t("paymentSuccess.backHome", "返回首頁")}
      </button>
    </div>
  );
};

export default PaymentSuccessPage;